/**
 * /api/mentor 导师端接口封装（三入口前缀隔离）。
 *
 * 覆盖：auth / workbench / profile / bookings / schedules / services / reviews / settlements。
 * 所有类型均来自 OpenAPI 契约生成（contracts/openapi.yaml → src/types/generated/api.d.ts）。
 */
import { requestJson } from '@/api/client'
import type { components } from '@/types/generated/api'

// ==================== Types（从契约生成） ====================

export type LoginRequest = components['schemas']['LoginRequest']
export type LoginResult = components['schemas']['LoginResult']
export type MentorUser = components['schemas']['AppUserInfo']
export type MentorProfileInfo = components['schemas']['MentorProfileInfo']
export type MentorProfileUpdateRequest = components['schemas']['MentorProfileUpdateRequest']
export type WorkbenchOverviewData = components['schemas']['WorkbenchOverviewData']
export type MentorScheduleInfo = components['schemas']['MentorScheduleInfo']
export type CreateScheduleRequest = components['schemas']['CreateScheduleRequest']
export type MentorBookingInfo = components['schemas']['MentorBookingInfo']
export type MentorServiceInfo = components['schemas']['MentorServiceInfo']
export type MentorServiceCreateRequest = components['schemas']['MentorServiceCreateRequest']
export type MentorServiceUpdateRequest = components['schemas']['MentorServiceUpdateRequest']
export type MentorReviewInfo = components['schemas']['MentorReviewInfo']
export type ReviewSummaryData = components['schemas']['ReviewSummaryData']
export type SettlementInfo = components['schemas']['SettlementInfo']
export type SettlementSummaryData = components['schemas']['SettlementSummaryData']
export type AgreementSignResponse = components['schemas']['AgreementSignResponse']
export type AgreementStatusResponse = components['schemas']['AgreementStatusResponse']

// ==================== API ====================

export const mentorApi = {
  auth: {
    /** 导师登录 */
    login(payload: LoginRequest): Promise<LoginResult> {
      return requestJson<LoginResult>({
        url: '/api/mentor/auth/login',
        method: 'POST',
        data: payload,
      })
    },
    /** 当前导师信息 */
    me(): Promise<MentorUser> {
      return requestJson<MentorUser>({
        url: '/api/mentor/auth/me',
        method: 'GET',
      })
    },
  },

  workbench: {
    /** 工作台概览 */
    overview(): Promise<WorkbenchOverviewData> {
      return requestJson<WorkbenchOverviewData>({
        url: '/api/mentor/workbench/overview',
        method: 'GET',
      })
    },
  },

  profile: {
    /** 获取当前导师档案 */
    get(): Promise<MentorProfileInfo> {
      return requestJson<MentorProfileInfo>({
        url: '/api/mentor/profile',
        method: 'GET',
      })
    },
    /** 更新导师档案 */
    update(data: MentorProfileUpdateRequest): Promise<MentorProfileInfo> {
      return requestJson<MentorProfileInfo>({
        url: '/api/mentor/profile',
        method: 'PUT',
        data,
      })
    },
    /** 签署导师协议（FR-MTR-01） */
    signAgreement(version?: string): Promise<AgreementSignResponse> {
      return requestJson<AgreementSignResponse>({
        url: '/api/mentor/profile/agreement/sign',
        method: 'POST',
        data: version ? { version } : undefined,
      })
    },
    /** 查询协议签署状态（FR-MTR-01） */
    agreementStatus(): Promise<AgreementStatusResponse> {
      return requestJson<AgreementStatusResponse>({
        url: '/api/mentor/profile/agreement/status',
        method: 'GET',
      })
    },
  },

  bookings: {
    /** 预约列表（可按状态筛选） */
    list(status?: string): Promise<MentorBookingInfo[]> {
      return requestJson<MentorBookingInfo[]>({
        url: '/api/mentor/bookings',
        method: 'GET',
        params: status ? { status } : undefined,
      })
    },
    /** 预约详情 */
    get(id: number): Promise<MentorBookingInfo> {
      return requestJson<MentorBookingInfo>({
        url: `/api/mentor/bookings/${id}`,
        method: 'GET',
      })
    },
    /** 确认预约（PENDING → CONFIRMED） */
    confirm(id: number, videoUrl?: string): Promise<MentorBookingInfo> {
      return requestJson<MentorBookingInfo>({
        url: `/api/mentor/bookings/${id}/confirm`,
        method: 'POST',
        data: videoUrl ? { videoUrl } : undefined,
      })
    },
    /** 完成预约（CONFIRMED → COMPLETED） */
    complete(id: number, mentorFeedback?: string): Promise<MentorBookingInfo> {
      return requestJson<MentorBookingInfo>({
        url: `/api/mentor/bookings/${id}/complete`,
        method: 'POST',
        data: mentorFeedback ? { mentorFeedback } : undefined,
      })
    },
    /** 取消预约 */
    cancel(id: number, reason: string): Promise<MentorBookingInfo> {
      return requestJson<MentorBookingInfo>({
        url: `/api/mentor/bookings/${id}/cancel`,
        method: 'POST',
        data: { reason },
      })
    },
    /** 服务交付（FR-MTR-05） */
    deliver(id: number, deliveryType: string, meetingLink?: string): Promise<MentorBookingInfo> {
      return requestJson<MentorBookingInfo>({
        url: `/api/mentor/bookings/${id}/deliver`,
        method: 'POST',
        data: { deliveryType, meetingLink },
      })
    },
  },

  schedules: {
    /** 排期列表 */
    list(): Promise<MentorScheduleInfo[]> {
      return requestJson<MentorScheduleInfo[]>({
        url: '/api/mentor/schedules',
        method: 'GET',
      })
    },
    /** 新增排期 */
    create(data: CreateScheduleRequest): Promise<MentorScheduleInfo> {
      return requestJson<MentorScheduleInfo>({
        url: '/api/mentor/schedules',
        method: 'POST',
        data,
      })
    },
    /** 更新排期 */
    update(id: number, data: CreateScheduleRequest): Promise<MentorScheduleInfo> {
      return requestJson<MentorScheduleInfo>({
        url: `/api/mentor/schedules/${id}`,
        method: 'PUT',
        data,
      })
    },
    /** 启用/停用排期 */
    toggle(id: number, enabled: number): Promise<void> {
      return requestJson<void>({
        url: `/api/mentor/schedules/${id}/toggle`,
        method: 'PUT',
        params: { enabled },
      })
    },
    /** 删除排期 */
    delete(id: number): Promise<void> {
      return requestJson<void>({
        url: `/api/mentor/schedules/${id}`,
        method: 'DELETE',
      })
    },
  },

  services: {
    /** 服务列表（含下架，FR-MTR-09） */
    list(): Promise<MentorServiceInfo[]> {
      return requestJson<MentorServiceInfo[]>({
        url: '/api/mentor/services',
        method: 'GET',
      })
    },
    /** 新增服务（B1：同导师同类型唯一） */
    create(data: MentorServiceCreateRequest): Promise<MentorServiceInfo> {
      return requestJson<MentorServiceInfo>({
        url: '/api/mentor/services',
        method: 'POST',
        data,
      })
    },
    /** 更新服务（仅 price/enabled 可改，FR-MTR-11） */
    update(id: number, data: MentorServiceUpdateRequest): Promise<MentorServiceInfo> {
      return requestJson<MentorServiceInfo>({
        url: `/api/mentor/services/${id}`,
        method: 'PUT',
        data,
      })
    },
    /** 上架/下架 */
    toggle(id: number, enabled: number): Promise<MentorServiceInfo> {
      return requestJson<MentorServiceInfo>({
        url: `/api/mentor/services/${id}/toggle`,
        method: 'PUT',
        params: { enabled },
      })
    },
    /** 删除服务（B3：有进行中预约拒绝） */
    delete(id: number): Promise<void> {
      return requestJson<void>({
        url: `/api/mentor/services/${id}`,
        method: 'DELETE',
      })
    },
  },

  reviews: {
    /** 评价列表 */
    list(): Promise<MentorReviewInfo[]> {
      return requestJson<MentorReviewInfo[]>({
        url: '/api/mentor/reviews',
        method: 'GET',
      })
    },
    /** 评价统计 */
    summary(): Promise<ReviewSummaryData> {
      return requestJson<ReviewSummaryData>({
        url: '/api/mentor/reviews/summary',
        method: 'GET',
      })
    },
    /** 回复评价 */
    reply(id: number, replyContent: string): Promise<void> {
      return requestJson<void>({
        url: `/api/mentor/reviews/${id}/reply`,
        method: 'POST',
        data: { replyContent },
      })
    },
  },

  settlements: {
    /** 结算记录列表 */
    list(): Promise<SettlementInfo[]> {
      return requestJson<SettlementInfo[]>({
        url: '/api/mentor/settlements',
        method: 'GET',
      })
    },
    /** 收入概览 */
    summary(): Promise<SettlementSummaryData> {
      return requestJson<SettlementSummaryData>({
        url: '/api/mentor/settlements/summary',
        method: 'GET',
      })
    },
  },
}
